// "use client";

// import WhatsAppLink from "./whatsapp-component";
import { useState } from "react";
import { HEADER } from "content/layout";
import { GetStaticProps } from "next";
import { getRegistrationFormState } from "@/lib/data";
import { IRegistrationSetting } from "@/models/FormSetting";

export const getStaticProps: GetStaticProps<{
  regiterationState: IRegistrationSetting | null;
}> = async () => {
  try {
    const regiterationState = await getRegistrationFormState();
    return { props: { regiterationState } };
  } catch (error) {
    console.error("Failed to load registration form state:", error);
    return {
      props: { regiterationState: null }
    };
  }
};
const DEFAULT_WHATSAPP_GROUP_LINK =
  process.env.NEXT_PUBLIC_ARC_WHATSAPP_GROUP_LINK || "https://chat.whatsapp.com/Br6BWjF0cB3Cfj1BbBJHGH?mode=gi_t";




// const isRegistrationOpen = now > deadline;

// const isRegistrationOpen = false;

// Validation constants and utilities
const FIELD_VALIDATIONS = {
  teamName: {
    pattern: /^[\u0621-\u064AA-Za-z\s]{2,}$/,
    message: "Name should only contain Arabic or English letters and single spaces",
    doubleSpaceMessage: "Name should not contain consecutive spaces",
  },
  name: {
    pattern: /^[\u0621-\u064AA-Za-z\s]{2,}$/,
    message: "Name should only contain Arabic or English letters and single spaces",
    doubleSpaceMessage: "Name should not contain consecutive spaces",
  },
  phone: {
    pattern: /^(010|011|012|015)\d{8}$/,
    message: "Phone number must start with (010, 011, 012, 015) and be 11 digits",
  },
  email: {
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    message: "Please enter a valid email address",
  },
  nationalID: {
    pattern: /^\d{14}$/,
    message: "National ID must be exactly 14 numbers",
  },
  institution: {
    pattern: /^[A-Za-z\s&\-.()]{2,}$/,
    message: "Please enter a valid institution name (letters, spaces, and common symbols only)",
  },
  government: {
    pattern: /^[\u0621-\u064AA-Za-z\s]{2,}$/,
    message: "Government Name should only contain Arabic or English letters and single spaces",
    doubleSpaceMessage: "GovernmentName should not contain consecutive spaces",
  },
};

// Unified validation system
const validate = (value, fieldType) => {
  if (!fieldType || !FIELD_VALIDATIONS[fieldType]) return "";

  const validation = FIELD_VALIDATIONS[fieldType];

  // Check for double spaces first if applicable
  if (validation.doubleSpaceMessage && value.includes("  ")) {
    return validation.doubleSpaceMessage;
  }

  // Then check pattern
  if (!validation.pattern.test(value)) {
    return validation.message;
  }

  return "";
};

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  return (
    <div
      className={`animate-slideIn fixed right-4 top-4 z-50 rounded-lg p-4 shadow-lg
                    backdrop-blur-md transition-all duration-300
                    ${type === "success"
          ? "border border-green-400/30 bg-transparent text-white"
          : "border border-red-400/30 bg-red-500/90 text-white"
        }`}
    >
      <div className="flex items-center gap-3">
        <span className="text-xl">{type === "success" ? "✨" : "❌"}</span>
        <p className="font-medium">{message}</p>
        <button
          onClick={onClose}
          className="ml-4 text-white/80 transition-colors hover:text-white"
          aria-label="Close notification"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default function Forms({ regiterationState }) {
  const isRegistrationOpen = Boolean(regiterationState?.isOpen);
  // Initial state
  const [team, setTeam] = useState({
    teamName: "",
    members: [
      {
        name: "",
        phone: "",
        email: "",
        nationalID: "",
        studyYear: "",
        university: "",
        faculty: "",
        government: "",
      },
      {
        name: "",
        phone: "",
        email: "",
        nationalID: "",
        studyYear: "",
        university: "",
        faculty: "",
        government: "",
      },
    ],
  });

  const TEAM_MEMBER_LIMIT = 5;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [registrationSuccess, setRegistrationSuccess] = useState({
    show: false,
    joinLink: "",
  });

  // Field type mapping
  const getFieldType = (fieldName) => {
    const fieldTypeMap = {
      teamName: "teamName",
      name: "name",
      phone: "phone",
      email: "email",
      nationalID: "nationalID",
      university: "institution",
      faculty: "institution",
      government: "government",
    };

    return fieldTypeMap[fieldName] || fieldName;
  };

  // Simplified handle change function
  const handleChange = (e, index) => {
    const { name, value } = e.target;

    if (index !== undefined) {
      // Member field validation
      const fieldType = getFieldType(name);
      const validationError = validate(value, fieldType);

      setErrors((prev) => ({
        ...prev,
        [`member${index}_${name}`]: validationError,
      }));

      const newMembers = [...team.members];
      newMembers[index][name] = value;
      setTeam({ ...team, members: newMembers });
    } else {
      // Team field validation
      const fieldType = getFieldType(name);
      const validationError = validate(value, fieldType);

      setErrors((prev) => ({
        ...prev,
        [name]: validationError,
      }));

      setTeam({ ...team, [name]: value });
    }
  };

  // Add member
  const addMember = () => {
    if (team.members.length < TEAM_MEMBER_LIMIT) {
      setTeam({
        ...team,
        members: [
          ...team.members,
          {
            name: "",
            phone: "",
            email: "",
            nationalID: "",
            studyYear: "",
            university: "",
            faculty: "",
            government: "",
          },
        ],
      });
    }
  };

  // Remove member
  const removeMember = (index) => {
    if (team.members.length > 1) {
      const newMembers = team.members.filter((_, i) => i !== index);
      setTeam({ ...team, members: newMembers });

      // Clear errors for removed member
      const newErrors = { ...errors };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(`member${index}_`)) {
          delete newErrors[key];
        }
      });
      setErrors(newErrors);
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Validate team name
    const teamNameError = validate(team.teamName, "teamName");
    if (teamNameError) {
      newErrors.teamName = teamNameError;
    }

    // Validate all members
    team.members.forEach((member, index) => {
      // Simple fields validation
      ["name", "phone", "email", "nationalID", "university", "faculty", "government"].forEach(
        (field) => {
          const fieldType = getFieldType(field);
          const error = validate(member[field], fieldType);
          if (error) {
            newErrors[`member${index}_${field}`] = error;
          }
        }
      );

      // Study Year special case
      if (!member.studyYear) {
        newErrors[`member${index}_studyYear`] = "Please select a study year";
      }
    });

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setNotification({
        show: true,
        message: "Please fix all errors before submitting",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          teamName: team.teamName,
          members: team.members,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const joinLink = data.whatsappGroupLink || data.whatsappLink || DEFAULT_WHATSAPP_GROUP_LINK;

        setNotification({
          show: true,
          message: data.message || "Registration successful! 🎉",
          type: "success",
        });
        setRegistrationSuccess({ show: true, joinLink });

        // Reset form after 2 seconds
        setTimeout(() => {
          setTeam({
            teamName: "",
            members: [
              {
                name: "",
                phone: "",
                email: "",
                nationalID: "",
                studyYear: "",
                university: "",
                faculty: "",
                government: "",
              },
              {
                name: "",
                phone: "",
                email: "",
                nationalID: "",
                studyYear: "",
                university: "",
                faculty: "",
                government: "",
              },
            ],
          });
          setErrors({});
        }, 2000);
      } else {
        throw new Error(data.message || "Registration failed");
      }
    } catch (error) {
      setNotification({
        show: true,
        message: error.message || "Failed to register. Please try again.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);

      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 5000);
    }
  };

  return (
    <section className="relative min-h-screen">
      {/* Background pattern matching your website */}
      <div className="absolute inset-0 -z-10 bg-white dark:bg-gray-900" />

      <div className="container">
        {/* Header */}
        <header className="mb-10 text-center">
          <h1 className="heading-hero uppercase">
            ARC <span className="text-orange-600"> 10</span> Form
            <span className="heading-pre ml-2 text-sm uppercase tracking-wide">Registration</span>
          </h1>
          <div className="mt-6 space-y-2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50">
              Be Ready For The Challenge
            </h2>
            <p className="text-gray-500 dark:text-gray-400">Aswan Robotics Competition</p>
          </div>
        </header>

        {!isRegistrationOpen ? (
          <ClosedRegistration />
        ) : registrationSuccess.show ? (
          <RegistrationSuccess joinLink={registrationSuccess.joinLink} />
        ) : (
          <div className="mx-auto max-w-4xl">
            <form
              onSubmit={handleSubmit}
              className="card space-y-8 rounded-lg border border-gray-200
                        bg-white p-6 shadow-lg transition-shadow hover:shadow-xl
                        dark:border-gray-700 dark:bg-gray-800 md:p-8"
            >
              {/* Form Header */}
              <div className="border-b border-gray-200 pb-6 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50">
                  Team Registration
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Form your squad (up to 5 members)
                </p>
              </div>

              {/* Team Name */}
              <div className="space-y-2">
                <label
                  htmlFor="teamName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Team Name
                </label>
                <input
                  id="teamName"
                  type="text"
                  name="teamName"
                  value={team.teamName}
                  onChange={(e) => handleChange(e, undefined)}
                  className={`input-field w-full px-4 py-3 !bg-transparent 
                            ${errors.teamName ? "border-red-500 dark:border-red-500" : ""}`}
                  placeholder="Enter your team name"
                  required
                />
                {errors.teamName && <p className="text-sm text-red-500">{errors.teamName}</p>}
              </div>

              {/* Team Members */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                  Team Members
                </h3>

                {team.members.map((member, index) => (
                  <div
                    key={index}
                    className="card space-y-4 rounded-md border border-gray-200 bg-gray-50 p-6 transition-colors hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:border-gray-600"
                  >
                    {/* Member Header */}
                    <div className="flex items-center justify-between border-b border-gray-200 pb-3 dark:border-gray-700">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-50">
                        {index === 0 ? "Team Leader" : `Member ${index + 1}`}
                      </h4>
                      {team.members.length > 2 && index > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMember(index)}
                          className="text-sm text-red-500 transition-colors hover:text-red-600
                                  dark:text-red-400 dark:hover:text-red-300"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {/* Member Fields */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={member.name}
                          onChange={(e) => handleChange(e, index)}
                          className={`input-field w-full px-4 py-3 !bg-transparent
                                     ${errors[`member${index}_name`] ? "border-red-500" : ""}`}
                          placeholder="Full Name"
                          required
                        />
                        {errors[`member${index}_name`] && (
                          <p className="text-sm text-red-500">{errors[`member${index}_name`]}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Phone Number
                        </label>
                        <input
                          type="text"
                          name="phone"
                          value={member.phone}
                          onChange={(e) => handleChange(e, index)}
                          className={`input-field w-full px-4 py-3 !bg-transparent
                                     ${errors[`member${index}_phone`] ? "border-red-500" : ""}`}
                          placeholder="01012345678"
                          required
                        />
                        {errors[`member${index}_phone`] && (
                          <p className="text-sm text-red-500">{errors[`member${index}_phone`]}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={member.email}
                          onChange={(e) => handleChange(e, index)}
                          className={`input-field w-full px-4 py-3 !bg-transparent
                                     ${errors[`member${index}_email`] ? "border-red-500" : ""}`}
                          placeholder="email@example.com"
                          required
                        />
                        {errors[`member${index}_email`] && (
                          <p className="text-sm text-red-500">{errors[`member${index}_email`]}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          National ID
                        </label>
                        <input
                          type="text"
                          name="nationalID"
                          value={member.nationalID}
                          onChange={(e) => handleChange(e, index)}
                          className={`input-field w-full px-4 py-3 !bg-transparent
                                    ${errors[`member${index}_nationalID`] ? "border-red-500" : ""
                            }`}
                          placeholder="14 digits"
                          maxLength={14}
                          required
                        />
                        {errors[`member${index}_nationalID`] && (
                          <p className="text-sm text-red-500">
                            {errors[`member${index}_nationalID`]}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          University/School
                        </label>
                        <input
                          type="text"
                          name="university"
                          value={member.university}
                          onChange={(e) => handleChange(e, index)}
                          className={`input-field w-full px-4 py-3 !bg-transparent
                                     ${errors[`member${index}_university`] ? "border-red-500" : ""
                            }`}
                          placeholder="Institution name"
                          required
                        />
                        {errors[`member${index}_university`] && (
                          <p className="text-sm text-red-500">
                            {errors[`member${index}_university`]}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Year of Study
                        </label>
                        <select
                          name="studyYear"
                          value={member.studyYear}
                          onChange={(e) => handleChange(e, index)}
                          className="input-field w-full px-4 py-3"
                          required
                        >
                          <option value="" disabled>
                            Select year
                          </option>
                          <option value="prep">Preparatory</option>
                          <option value="first">First Year</option>
                          <option value="second">Second Year</option>
                          <option value="third">Third Year</option>
                          <option value="fourth">Fourth Year</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Faculty/Department
                        </label>
                        <input
                          type="text"
                          name="faculty"
                          value={member.faculty}
                          onChange={(e) => handleChange(e, index)}
                          className={`input-field w-full px-4 py-3 !bg-transparent
                                    ${errors[`member${index}_faculty`] ? "border-red-500" : ""}`}
                          placeholder="Faculty name"
                          required
                        />
                        {errors[`member${index}_faculty`] && (
                          <p className="text-sm text-red-500">{errors[`member${index}_faculty`]}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          City/Governorate
                        </label>
                        <input
                          type="text"
                          name="government"
                          value={member.government}
                          onChange={(e) => handleChange(e, index)}
                          className={`input-field w-full px-4 py-3 !bg-transparent
                                     ${errors[`member${index}_government`] ? "border-red-500" : ""
                            }`}
                          placeholder="Your city"
                          required
                        />
                        {errors[`member${index}_government`] && (
                          <p className="text-sm text-red-500">
                            {errors[`member${index}_government`]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Member Button */}
              {team.members.length < TEAM_MEMBER_LIMIT && (
                <button
                  type="button"
                  onClick={addMember}
                  className="w-full rounded-md border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800/50 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-700/50"
                >
                  + Add Team Member
                </button>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-md bg-orange-500 px-6 py-3 font-medium
                         text-white shadow transition-all duration-200
                         hover:bg-orange-600 hover:shadow-lg
                         focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2
                         disabled:cursor-not-allowed disabled:opacity-50
                         dark:bg-orange-600 dark:hover:bg-orange-700
                         dark:focus:ring-offset-gray-800"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  "Register Team"
                )}
              </button>
            </form>
          </div>
        )}

        {/* Toast Notifications */}
        {notification.show && (
          <Toast
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
          />
        )}

        {/* Loading Overlay */}
        {isSubmitting && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
              <div className="flex items-center gap-3">
                <svg className="h-8 w-8 animate-spin text-orange-500" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-lg font-medium text-gray-900 dark:text-gray-50">
                  Processing registration...
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const ClosedRegistration = () => {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <div
        className="card rounded-lg border border-gray-200 bg-white p-8 shadow-lg
                      dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="mb-6">
          <span className="w-32 sm:w-40 md:w-48">{HEADER.logo.title}</span>
        </div>

        <h2 className="mb-4 text-3xl font-bold text-gray-900 dark:text-gray-50">
          Registration Closed
        </h2>

        <p className="mb-6 text-lg text-gray-500 dark:text-gray-400">
          Follow us on social media for future events!
        </p>

        <div className="flex justify-center gap-4 border-t border-gray-200 pt-6 dark:border-gray-700">
          <a
            href="https://www.facebook.com/share/19gAeagM9n/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 transition-colors hover:text-orange-500 dark:text-gray-400"
          >
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.77,7.46H14.5v-1.9c0-.9.6-1.1,1-1.1h3V.5h-4.33C10.24.5,9.5,3.44,9.5,5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4Z" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};

const RegistrationSuccess = ({ joinLink }) => {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <div
        className="card rounded-lg border border-gray-200 bg-white p-8 shadow-lg
                      dark:border-gray-700 dark:bg-gray-800"
      >
        <h2 className="mb-3 text-3xl font-bold text-gray-900 dark:text-gray-50">
          Registration Submitted Successfully
        </h2>
        <p className="mb-6 text-lg text-gray-500 dark:text-gray-400">
          Your team is registered. Join the WhatsApp group to receive updates and next steps.
        </p>

        <a
          href={joinLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-green-700"
        >
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20.52 3.48A11.82 11.82 0 0 0 12.04 0C5.5 0 .17 5.33.17 11.87c0 2.09.55 4.14 1.58 5.94L0 24l6.35-1.67a11.86 11.86 0 0 0 5.68 1.45h.01c6.54 0 11.87-5.33 11.87-11.87 0-3.17-1.23-6.15-3.39-8.43Zm-8.48 18.3h-.01a9.84 9.84 0 0 1-5.02-1.38l-.36-.21-3.77.99 1-3.68-.23-.38a9.84 9.84 0 0 1-1.51-5.24c0-5.43 4.42-9.85 9.86-9.85 2.64 0 5.12 1.02 6.98 2.89a9.77 9.77 0 0 1 2.88 6.97c0 5.44-4.42 9.86-9.85 9.86Zm5.4-7.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.08-.3-.15-1.26-.46-2.4-1.47-.88-.79-1.48-1.76-1.66-2.06-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.38-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.38-.27.3-1.04 1.01-1.04 2.47 0 1.45 1.06 2.86 1.2 3.06.15.2 2.08 3.17 5.03 4.44.7.3 1.25.48 1.67.62.7.22 1.34.19 1.85.12.56-.08 1.76-.72 2.01-1.41.25-.7.25-1.3.17-1.42-.08-.13-.27-.2-.57-.35Z" />
          </svg>
          Join WhatsApp Group
        </a>
      </div>
    </div>
  );
};
